<template>
  <div class="page">
    <div class="bg-grid" />
    <TopBar />
    <DashboardNav />

    <main class="content">
      <div class="page-header">
        <SectionHeader label="Model Lab" :meta="`${ai_clients.length} clients`" />
        <button class="add-btn" @click="showModal = true">+ ADD CLIENT</button>
      </div>

      <!-- AI Client Cards (4-column) -->
      <div v-if="ai_clients.length === 0" class="empty-state">
        <p class="empty-title">No AI clients yet</p>
        <p class="empty-body">Add your first AI client to start routing requests to Claude, GPT-4, Gemini and more.</p>
        <button class="empty-cta" @click="showModal = true">+ ADD CLIENT</button>
      </div>

      <div v-else class="client-grid">
        <div
          v-for="client in ai_clients"
          :key="client.id"
          class="client-card"
          :style="{ borderLeftColor: providerColor(client.provider) }"
        >
          <div class="card-head">
            <div class="client-meta">
              <span class="client-name">{{ client.name }}</span>
              <span class="client-model">{{ client.model }}</span>
            </div>
            <div class="card-head-right">
              <span class="provider-badge" :style="{ background: providerDim(client.provider), color: providerColor(client.provider) }">
                {{ client.provider }}
              </span>
              <button class="delete-btn" :title="`Delete ${client.name}`" @click="confirmDelete(client)">✕</button>
            </div>
          </div>

          <div class="card-stats">
            <div class="stat">
              <span class="stat-label">MESSAGES</span>
              <span class="stat-value">{{ client.usage.messages.toLocaleString() }}</span>
            </div>
            <div class="stat">
              <span class="stat-label">TOTAL COST</span>
              <span class="stat-value">${{ client.usage.total_cost.toFixed(4) }}</span>
            </div>
            <div class="stat">
              <span class="stat-label">AVG LATENCY</span>
              <span class="stat-value">
                {{ client.usage.avg_latency > 0 ? client.usage.avg_latency + "ms" : "—" }}
              </span>
            </div>
          </div>

          <div class="card-footer">
            <span class="token-hint">key ···· {{ client.token_hint || "····" }}</span>
            <div v-if="projectsFor(client.id).length > 0" class="card-projects">
              <span
                v-for="a in projectsFor(client.id)"
                :key="a.id"
                class="project-chip"
              >
                {{ a.project.name }} · {{ a.purpose }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Routing Rules -->
      <div v-if="assignments.length > 0" class="section">
        <SectionHeader label="Routing Rules" :meta="`${assignments.length} assignments`" />
        <div class="routing-table">
          <div class="table-header">
            <span class="col-project">PROJECT</span>
            <span class="col-purpose">PURPOSE</span>
            <span class="col-client">CLIENT</span>
            <span class="col-model">MODEL</span>
            <span class="col-default">DEFAULT</span>
          </div>
          <div v-for="a in assignments" :key="a.id" class="table-row">
            <span class="col-project row-text">{{ a.project.name }}</span>
            <span class="col-purpose">
              <span class="purpose-badge">{{ a.purpose }}</span>
            </span>
            <span class="col-client row-text">{{ a.ai_client.name }}</span>
            <span class="col-model model-text">{{ a.ai_client.model }}</span>
            <span class="col-default">
              <span v-if="a.is_default" class="default-dot" />
            </span>
          </div>
        </div>
      </div>

      <!-- Value Matrix -->
      <div v-if="ai_clients.length > 0" class="section">
        <SectionHeader label="Value Matrix" meta="cost vs messages" />
        <div class="matrix-grid">
          <div
            v-for="client in ai_clients"
            :key="client.id"
            class="matrix-row"
          >
            <span class="matrix-name">{{ client.name }}</span>
            <ProgressBar
              :value="client.usage.messages"
              :max="maxMessages"
              :color="providerBarColor(client.provider)"
            />
            <span class="matrix-count mono">{{ client.usage.messages.toLocaleString() }}</span>
            <span class="matrix-cost mono">${{ client.usage.total_cost.toFixed(4) }}</span>
          </div>
        </div>
      </div>
    </main>

    <!-- Add Client Modal -->
    <Teleport to="body">
      <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
        <div class="modal">
          <div class="modal-header">
            <span class="modal-title">ADD AI CLIENT</span>
            <button class="modal-close" @click="closeModal">✕</button>
          </div>

          <form class="modal-form" @submit.prevent="createClient">
            <div class="field">
              <label class="field-label">NAME</label>
              <input
                v-model="form.name"
                class="field-input"
                type="text"
                placeholder="e.g. Anthropic Sonnet Production"
                required
                :disabled="creating"
              />
            </div>

            <div class="field">
              <label class="field-label">PROVIDER</label>
              <select v-model="form.provider" class="field-input field-select" required :disabled="creating" @change="form.model = ''">
                <option value="">Select provider…</option>
                <option value="anthropic">Anthropic</option>
                <option value="openai">OpenAI</option>
                <option value="google">Google</option>
              </select>
            </div>

            <div class="field">
              <label class="field-label">MODEL</label>
              <select v-if="modelOptions.length" v-model="form.model" class="field-input field-select" required :disabled="creating">
                <option value="">Select model…</option>
                <option v-for="m in modelOptions" :key="m" :value="m">{{ m }}</option>
              </select>
              <input
                v-else
                v-model="form.model"
                class="field-input"
                type="text"
                placeholder="e.g. claude-sonnet-4-6"
                required
                :disabled="creating"
              />
            </div>

            <div class="field">
              <label class="field-label">API KEY / TOKEN</label>
              <input
                v-model="form.token"
                class="field-input mono"
                type="password"
                :placeholder="tokenPlaceholder"
                required
                autocomplete="off"
                :disabled="creating"
              />
              <span class="field-hint">Stored encrypted — only the last 4 characters are ever shown.</span>
            </div>

            <div v-if="createError" class="form-error">{{ createError }}</div>

            <div class="modal-actions">
              <button type="button" class="cancel-btn" :disabled="creating" @click="closeModal">
                CANCEL
              </button>
              <button type="submit" class="submit-btn" :disabled="creating">
                {{ creating ? "ADDING…" : "ADD CLIENT" }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Teleport>

    <!-- Delete confirm modal -->
    <Teleport to="body">
      <div v-if="deleteTarget" class="modal-overlay" @click.self="deleteTarget = null">
        <div class="modal modal-sm">
          <div class="modal-header">
            <span class="modal-title">REMOVE CLIENT</span>
            <button class="modal-close" @click="deleteTarget = null">✕</button>
          </div>
          <div class="modal-body">
            <p class="confirm-text">
              Remove <strong>{{ deleteTarget.name }}</strong>?
              This cannot be undone. Clients assigned to projects cannot be removed.
            </p>
            <div v-if="deleteError" class="form-error">{{ deleteError }}</div>
            <div class="modal-actions">
              <button class="cancel-btn" :disabled="deleting" @click="deleteTarget = null">CANCEL</button>
              <button class="danger-btn" :disabled="deleting" @click="deleteClient">
                {{ deleting ? "REMOVING…" : "REMOVE" }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { computed, ref } from "vue"
import { router } from "@inertiajs/vue3"
import TopBar from "../../components/TopBar.vue"
import DashboardNav from "../../components/DashboardNav.vue"
import SectionHeader from "../../components/SectionHeader.vue"
import ProgressBar from "../../components/ProgressBar.vue"

const props = defineProps({
  ai_clients:  { type: Array, default: () => [] },
  assignments: { type: Array, default: () => [] },
})

// ---------------------------------------------------------------------------
// Add Client Modal
// ---------------------------------------------------------------------------
const showModal   = ref(false)
const creating    = ref(false)
const createError = ref(null)
const form = ref({ name: "", provider: "", model: "", token: "" })

const MODEL_MAP = {
  anthropic: [
    "claude-opus-4-6",
    "claude-sonnet-4-6",
    "claude-haiku-4-5-20251001",
  ],
  openai: [
    "gpt-4o",
    "gpt-4o-mini",
    "gpt-4-turbo",
    "gpt-3.5-turbo",
  ],
  google: [
    "gemini-2.0-flash",
    "gemini-1.5-pro",
    "gemini-1.5-flash",
  ],
}

const TOKEN_PLACEHOLDER = {
  anthropic: "sk-ant-api03-…",
  openai:    "sk-proj-…",
  google:    "AIza…",
}

const modelOptions    = computed(() => MODEL_MAP[form.value.provider] ?? [])
const tokenPlaceholder = computed(() => TOKEN_PLACEHOLDER[form.value.provider] ?? "Your API key…")

function closeModal() {
  if (creating.value) return
  showModal.value   = false
  createError.value = null
  form.value = { name: "", provider: "", model: "", token: "" }
}

async function createClient() {
  creating.value    = true
  createError.value = null

  try {
    const token = document.querySelector("meta[name='csrf-token']")?.content
    const res   = await fetch("/api/v1/ai_clients", {
      method:  "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
      body:    JSON.stringify(form.value),
    })
    const data = await res.json()

    if (res.ok) {
      showModal.value = false
      router.visit("/model-lab", { replace: true })
    } else {
      createError.value = data.message || "Failed to create client"
    }
  } catch {
    createError.value = "Network error — please try again"
  } finally {
    creating.value = false
  }
}

// ---------------------------------------------------------------------------
// Delete Client
// ---------------------------------------------------------------------------
const deleteTarget = ref(null)
const deleting     = ref(false)
const deleteError  = ref(null)

function confirmDelete(client) {
  deleteTarget.value = client
  deleteError.value  = null
}

async function deleteClient() {
  deleting.value    = true
  deleteError.value = null

  try {
    const token = document.querySelector("meta[name='csrf-token']")?.content
    const res   = await fetch(`/api/v1/ai_clients/${deleteTarget.value.id}`, {
      method:  "DELETE",
      headers: { "X-CSRF-Token": token },
    })
    const data = await res.json()

    if (res.ok) {
      deleteTarget.value = null
      router.visit("/model-lab", { replace: true })
    } else {
      deleteError.value = data.message || "Could not remove client"
    }
  } catch {
    deleteError.value = "Network error — please try again"
  } finally {
    deleting.value = false
  }
}

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------
function providerColor(p) {
  const map = { openai: "#4ade80", anthropic: "#60a5fa", google: "#c084fc" }
  return map[p?.toLowerCase()] ?? "#f5a623"
}

function providerDim(p) {
  const map = {
    openai:    "rgba(74,222,128,0.15)",
    anthropic: "rgba(96,165,250,0.15)",
    google:    "rgba(192,132,252,0.15)",
  }
  return map[p?.toLowerCase()] ?? "rgba(245,166,35,0.18)"
}

function providerBarColor(p) {
  const map = { openai: "green", anthropic: "blue", google: "purple" }
  return map[p?.toLowerCase()] ?? "amber"
}

function projectsFor(clientId) {
  return props.assignments.filter((a) => a.ai_client.id === clientId)
}

const maxMessages = computed(() =>
  Math.max(...props.ai_clients.map((c) => c.usage.messages), 1)
)
</script>

<style scoped>
.page {
  min-height: 100vh;
  background-color: #0a0a0b;
  position: relative;
}

.bg-grid {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  background-image:
    linear-gradient(rgba(245, 166, 35, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(245, 166, 35, 0.03) 1px, transparent 1px);
  background-size: 40px 40px;
}

.content {
  position: relative;
  z-index: 1;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: -8px;
}

.add-btn {
  padding: 7px 14px;
  background: rgba(245, 166, 35, 0.12);
  border: 1px solid rgba(245, 166, 35, 0.25);
  border-radius: 4px;
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 1.2px;
  color: #f5a623;
  cursor: pointer;
  transition: all 0.2s;
}

.add-btn:hover {
  background: rgba(245, 166, 35, 0.2);
  border-color: rgba(245, 166, 35, 0.4);
}

.empty-state {
  padding: 48px;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.025);
}

.empty-title {
  font-family: var(--font-display);
  font-size: 16px;
  font-weight: 700;
  color: #e8e6e3;
  margin: 0 0 6px;
}

.empty-body {
  font-family: var(--font-mono);
  font-size: 12px;
  color: rgba(232, 230, 227, 0.45);
  margin: 0 0 20px;
}

.empty-cta {
  padding: 9px 20px;
  background: #f5a623;
  border: none;
  border-radius: 4px;
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1px;
  color: #0a0a0b;
  cursor: pointer;
  transition: opacity 0.2s;
}

.empty-cta:hover { opacity: 0.85; }

.client-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

.client-card {
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-left-width: 3px;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.card-head-right {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.client-meta {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.client-name {
  font-family: var(--font-mono);
  font-size: 13px;
  font-weight: 600;
  color: #e8e6e3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.client-model {
  font-family: var(--font-mono);
  font-size: 10px;
  color: rgba(232, 230, 227, 0.35);
}

.provider-badge {
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  padding: 2px 7px;
  border-radius: 3px;
}

.delete-btn {
  background: none;
  border: none;
  color: rgba(232, 230, 227, 0.2);
  font-size: 11px;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 3px;
  line-height: 1;
  transition: color 0.15s, background 0.15s;
}

.delete-btn:hover {
  color: #f87171;
  background: rgba(248, 113, 113, 0.1);
}

.card-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.stat {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.stat-label {
  font-family: var(--font-mono);
  font-size: 8px;
  font-weight: 700;
  letter-spacing: 1.2px;
  color: rgba(232, 230, 227, 0.25);
}

.stat-value {
  font-family: var(--font-mono);
  font-size: 13px;
  font-weight: 700;
  color: #e8e6e3;
}

.card-footer {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.token-hint {
  font-family: var(--font-mono);
  font-size: 9px;
  color: rgba(232, 230, 227, 0.2);
  letter-spacing: 0.5px;
}

.card-projects {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.project-chip {
  font-family: var(--font-mono);
  font-size: 9px;
  color: rgba(232, 230, 227, 0.45);
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  padding: 2px 6px;
  border-radius: 3px;
}

.section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.routing-table {
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.025);
}

.table-header {
  display: grid;
  grid-template-columns: 1fr 100px 1fr 140px 60px;
  padding: 10px 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: rgba(232, 230, 227, 0.25);
}

.table-row {
  display: grid;
  grid-template-columns: 1fr 100px 1fr 140px 60px;
  align-items: center;
  padding: 11px 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  transition: background 0.15s;
}

.table-row:hover { background: rgba(245, 166, 35, 0.04); }

.row-text {
  font-family: var(--font-mono);
  font-size: 12px;
  color: #e8e6e3;
}

.model-text {
  font-family: var(--font-mono);
  font-size: 11px;
  color: rgba(232, 230, 227, 0.45);
}

.purpose-badge {
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  padding: 2px 7px;
  border-radius: 3px;
  background: rgba(96, 165, 250, 0.15);
  color: #60a5fa;
}

.default-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #4ade80;
  box-shadow: 0 0 6px rgba(74, 222, 128, 0.6);
}

.matrix-grid {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px 18px;
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
}

.matrix-row {
  display: grid;
  grid-template-columns: 160px 1fr 80px 80px;
  align-items: center;
  gap: 12px;
}

.matrix-name {
  font-family: var(--font-mono);
  font-size: 11px;
  color: rgba(232, 230, 227, 0.6);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mono {
  font-family: var(--font-mono);
  font-size: 11px;
  color: rgba(232, 230, 227, 0.45);
  text-align: right;
}

/* ── Modal ── */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal {
  background: #111113;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  width: 480px;
  max-width: calc(100vw - 32px);
}

.modal-sm { width: 400px; }

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.modal-title {
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1.5px;
  color: rgba(232, 230, 227, 0.45);
}

.modal-close {
  background: none;
  border: none;
  color: rgba(232, 230, 227, 0.3);
  font-size: 14px;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 3px;
  transition: color 0.15s;
}

.modal-close:hover { color: #e8e6e3; }

.modal-form {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.modal-body {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.confirm-text {
  font-family: var(--font-mono);
  font-size: 12px;
  color: rgba(232, 230, 227, 0.6);
  margin: 0;
  line-height: 1.6;
}

.confirm-text strong { color: #e8e6e3; }

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field-label {
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 1.2px;
  color: rgba(232, 230, 227, 0.35);
}

.field-input {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 4px;
  padding: 9px 12px;
  font-family: var(--font-mono);
  font-size: 12px;
  color: #e8e6e3;
  outline: none;
  transition: border-color 0.2s;
  width: 100%;
  box-sizing: border-box;
}

.field-input:focus { border-color: rgba(245, 166, 35, 0.4); }
.field-input::placeholder { color: rgba(232, 230, 227, 0.2); }
.field-select option { background: #111113; }

.field-hint {
  font-family: var(--font-mono);
  font-size: 9px;
  color: rgba(232, 230, 227, 0.25);
}

.form-error {
  font-family: var(--font-mono);
  font-size: 11px;
  color: #f87171;
  padding: 8px 12px;
  background: rgba(248, 113, 113, 0.1);
  border: 1px solid rgba(248, 113, 113, 0.2);
  border-radius: 4px;
}

.modal-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding-top: 4px;
}

.cancel-btn {
  padding: 8px 16px;
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 4px;
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1px;
  color: rgba(232, 230, 227, 0.45);
  cursor: pointer;
  transition: all 0.2s;
}

.cancel-btn:hover:not(:disabled) { border-color: rgba(255, 255, 255, 0.15); color: #e8e6e3; }
.cancel-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.submit-btn {
  padding: 8px 18px;
  background: #f5a623;
  border: none;
  border-radius: 4px;
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1px;
  color: #0a0a0b;
  cursor: pointer;
  transition: opacity 0.2s;
}

.submit-btn:hover:not(:disabled) { opacity: 0.85; }
.submit-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.danger-btn {
  padding: 8px 18px;
  background: rgba(248, 113, 113, 0.15);
  border: 1px solid rgba(248, 113, 113, 0.3);
  border-radius: 4px;
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1px;
  color: #f87171;
  cursor: pointer;
  transition: all 0.2s;
}

.danger-btn:hover:not(:disabled) { background: rgba(248, 113, 113, 0.25); }
.danger-btn:disabled { opacity: 0.4; cursor: not-allowed; }
</style>
